import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPause } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { updateData } from '../../../services';
import ConfirmDialog from '../../helpers/ConfirmDialog';

const FLAG_CONFIG = {
    manufacturing_hold: {
        icon: faPause,
        triggerTitle: 'Manufacturing Hold',
        triggerLabel: (isOn) =>
            isOn ? 'Release Manufacturing Hold' : 'Add Manufacturing Hold',
        title: 'Update Manufacturing Hold',
        message: 'Are you sure you want to toggle the manufacturing hold for this project?',
        detail: 'This action will update whether manufacturing is on hold for the project.',
        confirmLabel: 'Update Manufacturing Hold',
        successMessage: 'Manufacturing hold updated successfully!',
        errorDefault: 'Failed to update manufacturing hold. Please try again.',
    },
    engineering_hold: {
        icon: faPause,
        triggerTitle: 'Engineering Hold',
        triggerLabel: (isOn) =>
            isOn ? 'Release Engineering Hold' : 'Add Engineering Hold',
        title: 'Update Engineering Hold',
        message: 'Are you sure you want to toggle the engineering hold for this project?',
        detail: 'This action will update whether engineering is on hold for the project.',
        confirmLabel: 'Update Engineering Hold',
        successMessage: 'Engineering hold updated successfully!',
        errorDefault: 'Failed to update engineering hold. Please try again.',
    },
    shipping_hold: {
        icon: faPause,
        triggerTitle: 'Shipping Hold',
        triggerLabel: (isOn) => (isOn ? 'Release Shipping Hold' : 'Add Shipping Hold'),
        title: 'Update Shipping Hold',
        message: 'Are you sure you want to toggle the shipping hold for this project?',
        detail: 'This action will update whether shipping is on hold for the project.',
        confirmLabel: 'Update Shipping Hold',
        successMessage: 'Shipping hold updated successfully!',
        errorDefault: 'Failed to update shipping hold. Please try again.',
    },
    is_expedited: {
        icon: faExclamationTriangle,
        triggerTitle: 'Expedite Project',
        triggerLabel: (isOn) => (isOn ? 'Remove Expedited Status' : 'Expedite Project'),
        title: 'Update Expedited Status',
        message: 'Are you sure you want to toggle the expedited status for this project?',
        detail: 'This action will update the expedited status for the project.',
        confirmLabel: 'Update Expedited Status',
        successMessage: 'Project expedited status updated successfully!',
        errorDefault: 'Failed to update expedited status for project. Please try again.',
    },
};

export const ProjectFlagToggle = ({ project, field }) => {
    const { token, base_url, setProjects } = useAppContext();
    const config = FLAG_CONFIG[field];
    const isOn = Boolean(project[field]);

    if (!config) {
        console.error(`Unknown project flag field: ${field}`);
        return null;
    }

    return (
        <ConfirmDialog
            trigger={
                <button className="btn btn-secondary" title={config.triggerTitle}>
                    <FontAwesomeIcon icon={config.icon} />
                    &nbsp;
                    {config.triggerLabel(isOn)}
                </button>
            }
            title={config.title}
            message={config.message}
            entityLabel={`Project: ${project?.project_name}`}
            detail={config.detail}
            confirmLabel={config.confirmLabel}
            successMessage={config.successMessage}
            errorMessages={{
                notFound: 'Project not found',
                client: 'Invalid project data. Please check your input.',
                default: config.errorDefault,
            }}
            onConfirm={async () => {
                const updated_project = await updateData(
                    token,
                    `${base_url}/apis/projects/${project.id}`,
                    { [field]: !isOn }
                );

                if (!updated_project.project) return false;

                setProjects((prevProjects) =>
                    prevProjects.map((p) =>
                        p.id === project.id ? updated_project.project : p
                    )
                );
            }}
        />
    );
};

export const HoldProjectForm = ({ project }) => (
    <ProjectFlagToggle project={project} field="manufacturing_hold" />
);

export const EngineeringHoldProjectForm = ({ project }) => (
    <ProjectFlagToggle project={project} field="engineering_hold" />
);

export const ShippingHoldProjectForm = ({ project }) => (
    <ProjectFlagToggle project={project} field="shipping_hold" />
);

export const ExpediteProjectForm = ({ project }) => (
    <ProjectFlagToggle project={project} field="is_expedited" />
);
